import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";

import FloatingActions from "@/components/landing/FloatingActions";

import portalApi from "@/lib/portalApi";

import PortalHeader from "@/components/portal/PortalHeader";
import Stepper from "@/components/portal/Stepper";

import WelcomeStep from "@/components/portal/steps/WelcomeStep";
import PersonalStep from "@/components/portal/steps/PersonalStep";
import EmploymentStep from "@/components/portal/steps/EmploymentStep";
import ExistingLoansStep from "@/components/portal/steps/ExistingLoansStep";
import ReferencesStep from "@/components/portal/steps/ReferencesStep";
import AssetsStep from "@/components/portal/steps/AssetsStep";
import DocumentsStep from "@/components/portal/steps/DocumentsStep";
import DeclarationStep from "@/components/portal/steps/DeclarationStep";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STEPS = [
  "Welcome",
  "Personal Details",
  "Employment Details",
  "Existing Loans",
  "References",
  "Assets",
  "Document Upload",
  "Declaration",
];

const TERMINAL_APPLICATION_STATUSES = [
  "READY_FOR_SUBMISSION",
  "SUBMITTED_TO_BANK",
  "BANK_QUERY",
  "APPROVED",
  "REJECTED",
  "DISBURSED",
  "CLOSED",
];

const APPLICATION_STATUS_LABELS = {
  READY_FOR_SUBMISSION: "Ready for Submission",
  SUBMITTED_TO_BANK: "Submitted to Bank",
  BANK_QUERY: "Bank Query",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  DISBURSED: "Disbursed",
  CLOSED: "Closed",
};

const STEP_INDEX_BY_SECTION = {
  personal_details: 1,
  employment_details: 2,
  existing_loans: 3,
  references: 4,
  assets: 5,
  documents: 6,
  declaration: 7,
};

function looksLikeCompletePortalState(value) {
  if (!value || typeof value !== "object") {
    return false;
  }

  return Boolean(
    value.application_number ||
    value.application ||
    value.personal_details ||
    value.documents,
  );
}

function getCompletePortalState(response) {
  if (looksLikeCompletePortalState(response)) {
    return response;
  }

  if (response?.state && looksLikeCompletePortalState(response.state)) {
    return response.state;
  }

  return null;
}

function isFilled(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  return true;
}

function fieldProgress(object, fields) {
  if (!fields.length) {
    return 100;
  }

  const completed = fields.filter((field) => isFilled(object?.[field])).length;

  return Number(((completed / fields.length) * 100).toFixed(2));
}

function averageRowProgress(rows, fields, emptyProgress = 100) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return emptyProgress;
  }

  const total = rows.reduce((sum, row) => sum + fieldProgress(row, fields), 0);

  return Number((total / rows.length).toFixed(2));
}

function getPersonalProgress(state) {
  const primaryFields = [
    "fullName",
    "dateOfBirth",
    "gender",
    "maritalStatus",
    "nationality",
    "motherName",
    "fatherName",
    "spouseName",
    "qualification",
    "dependents",
    "monthlyIncome",
    "phone",
    "email",
    "currentAddress",
    "currentPincode",
    "houseOwnership",
    "permanentAddress",
    "permanentPincode",
  ];

  const primaryProgress = fieldProgress(state?.personal_details, primaryFields);

  const coApplicant = state?.co_applicant;

  if (!coApplicant) {
    return primaryProgress;
  }

  const coApplicantProgress = fieldProgress(coApplicant, primaryFields);

  return Number(((primaryProgress + coApplicantProgress) / 2).toFixed(2));
}

function getEmploymentProgress(state) {
  const employment = state?.employment_details || {};

  return fieldProgress(employment, [
    "employmentType",
    "totalWorkExperienceMonths",
    "currentJobPeriodMonths",
    "officeName",
    "officeAddress",
    "officePincode",
    "employerName",
    "employerAddress",
    "employerDesignation",
  ]);
}

function getExistingLoansProgress(state, completedSteps = []) {
  if (!completedSteps.includes(STEP_INDEX_BY_SECTION.existing_loans)) {
    return 0;
  }

  return averageRowProgress(
    state?.existing_loans,
    [
      "applicantType",
      "customerId",
      "loanAccountNumber",
      "bankName",
      "loanType",
      "status",
      "loanAmount",
      "interestRate",
      "emi",
      "outstandingBalance",
      "residualTenureMonths",
      "disbursementDate",
      "remarks",
    ],
    100,
  );
}

function getReferencesProgress(state, completedSteps = []) {
  if (!completedSteps.includes(STEP_INDEX_BY_SECTION.references)) {
    return 0;
  }

  return averageRowProgress(
    state?.references,
    ["name", "relationship", "address", "phone", "email", "remarks"],
    100,
  );
}

function getAssetsProgress(state, completedSteps = []) {
  if (!completedSteps.includes(STEP_INDEX_BY_SECTION.assets)) {
    return 0;
  }

  return averageRowProgress(
    state?.assets,
    ["type", "description", "value", "remarks"],
    100,
  );
}

function getDocumentsProgress(state) {
  const progress = state?.document_progress;

  if (!progress) {
    return 0;
  }

  return Math.max(0, Math.min(100, Number(progress.uploadPercentage ?? 0)));
}

export default function ClientPortal() {
  const { token } = useParams();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingBlockedDialogOpen, setEditingBlockedDialogOpen] =
    useState(false);

  const savedAt = useRef(null);

  const loadPortal = useCallback(
    async (showLoader = false) => {
      if (!token) {
        setError("This application link is invalid.");
        setLoading(false);
        return null;
      }

      if (showLoader) {
        setLoading(true);
      }

      try {
        const response = await portalApi.get(token);
        const completeState = getCompletePortalState(response);

        if (!completeState) {
          throw new Error("Invalid portal response.");
        }

        setState(completeState);
        setError(null);

        return completeState;
      } catch (err) {
        console.error("Failed to load client portal:", err);

        setError(
          "This application link is invalid or expired. Please contact your Money Mart advisor.",
        );

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const response = await portalApi.get(token);

        if (!alive) {
          return;
        }

        const completeState = getCompletePortalState(response);

        if (!completeState) {
          throw new Error("Invalid portal response.");
        }

        setState(completeState);
        setError(null);
      } catch (err) {
        if (!alive) {
          return;
        }

        console.error("Failed to load client portal:", err);

        setError(
          "This application link is invalid or expired. Please contact your Money Mart advisor.",
        );
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    const meta = document.createElement("meta");

    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";

    document.head.appendChild(meta);

    return () => {
      meta.remove();
    };
  }, []);

  const applyResponse = useCallback(
    (response) => {
      if (!response) {
        return;
      }

      const completeState = getCompletePortalState(response);

      if (completeState) {
        setState(completeState);
        return;
      }

      if (Object.prototype.hasOwnProperty.call(response, "current_step")) {
        setState((current) => ({
          ...(current || {}),
          current_step: response.current_step,
        }));

        return;
      }

      loadPortal(false).catch(() => {});
    },
    [loadPortal],
  );

  const submitted = state?.status === "submitted";

  const applicationEditable =
    state?.application_editable !== undefined
      ? Boolean(state.application_editable)
      : !TERMINAL_APPLICATION_STATUSES.includes(state?.application_status);

  const applicationStatus = state?.application_status || "";

  const applicationStatusLabel =
    APPLICATION_STATUS_LABELS[applicationStatus] ||
    applicationStatus
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const showEditingBlockedDialog = useCallback(() => {
    setEditingBlockedDialogOpen(true);
  }, []);

  const declarationLocked =
    state?.declaration_locked !== undefined
      ? Boolean(state.declaration_locked)
      : Boolean(
          state?.portal?.submitted_at &&
          !state?.portal?.declaration_resubmission_required,
        );

  const declarationResubmissionRequired = Boolean(
    state?.declaration_resubmission_required ??
    state?.portal?.declaration_resubmission_required,
  );

  /*
   * Data is persisted ONLY when a step explicitly calls patch().
   * There is no debounce/autosave here.
   */
  const patch = useCallback(
    async (section, data) => {
      if (!applicationEditable) {
        showEditingBlockedDialog();
        return null;
      }
      try {
        setSaving(true);

        const response = await portalApi.update(token, section, data);

        applyResponse(response);

        savedAt.current = new Date();

        return response;
      } catch (err) {
        console.error(`Failed to save portal section "${section}":`, err);

        const statusCode =
          err?.response?.status ??
          err?.status ??
          err?.response?.data?.statusCode;

        const errorMessage =
          err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "";

        const applicationLocked =
          statusCode === 400 &&
          (errorMessage === "This application can no longer be modified." ||
            errorMessage
              .toLowerCase()
              .includes("application can no longer be modified") ||
            errorMessage
              .toLowerCase()
              .includes("application has already been submitted"));

        if (applicationLocked) {
          /*
           * The admin may have changed the application status
           * while this portal was open.
           *
           * Refresh the state so application_status and
           * application_editable are immediately correct.
           */
          try {
            await loadPortal(false);
          } catch (refreshError) {
            console.error(
              "Failed to refresh portal after application lock:",
              refreshError,
            );
          }

          showEditingBlockedDialog();

          /*
           * IMPORTANT:
           * Do not rethrow this error.
           *
           * The user has been informed through the dialog,
           * so throwing here only creates the React runtime
           * error shown in the screenshot.
           */
          return null;
        }

        toast.error("Unable to save changes", {
          description: errorMessage || "Please retry.",
        });

        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      token,
      applyResponse,
      applicationEditable,
      showEditingBlockedDialog,
      loadPortal,
    ],
  );

  const currentStep = !applicationEditable
    ? declarationResubmissionRequired
      ? STEP_INDEX_BY_SECTION.declaration
      : 0
    : Math.max(0, Math.min(Number(state?.current_step ?? 0), STEPS.length - 1));

  const completedSteps = useMemo(() => {
    if (!state) {
      return [];
    }

    const persistedSteps = Array.isArray(state.completed_steps)
      ? state.completed_steps
          .map(Number)
          .filter(
            (stepIndex) =>
              Number.isInteger(stepIndex) &&
              stepIndex >= 0 &&
              stepIndex < STEPS.length,
          )
      : [];

    /*
     * Documents are NOT allowed to remain completed merely because
     * step 6 was previously persisted in completed_steps.
     *
     * The Documents step is derived from the current document
     * upload progress.
     *
     * Therefore always remove step 6 from the persisted list first.
     */
    const normalizedPersistedSteps = persistedSteps.filter(
      (stepIndex) => stepIndex !== STEP_INDEX_BY_SECTION.documents,
    );

    /*
     * Welcome has no editable fields, so it is
     * always considered completed.
     *
     * It does not need to be stored in completed_steps
     * in the database.
     */
    const documentUploadCompleted = getDocumentsProgress(state) >= 100;

    /*
     * Declaration remains completed after submission.
     *
     * Editing previous steps must NOT remove it.
     *
     * Only an explicit declaration resubmission request
     * can make this step incomplete again.
     */
    const declarationCompleted =
      Boolean(
        state?.declaration_accepted ?? state?.portal?.declaration_accepted,
      ) &&
      !Boolean(
        state?.declaration_resubmission_required ??
        state?.portal?.declaration_resubmission_required,
      );

    return Array.from(
      new Set([
        0,
        ...normalizedPersistedSteps,
        ...(documentUploadCompleted ? [STEP_INDEX_BY_SECTION.documents] : []),
        ...(declarationCompleted ? [STEP_INDEX_BY_SECTION.declaration] : []),
      ]),
    );
  }, [state]);

  const overallProgress = useMemo(() => {
    if (!state) {
      return 0;
    }

    const completedCount = completedSteps.length;

    if (!completedCount) {
      return 0;
    }

    return Math.min(100, Math.round((completedCount / STEPS.length) * 100));
  }, [state, completedSteps]);

  const stepLabels = STEPS;

  const handleDocumentsUpdated = useCallback(
    async (response) => {
      if (response) {
        applyResponse(response);
      } else {
        await loadPortal(false);
      }
    },
    [applyResponse, loadPortal],
  );

  const setStep = useCallback(
    async (nextStep) => {
      if (!state) {
        return;
      }

      if (!applicationEditable) {
        showEditingBlockedDialog();
        return;
      }

      const safeStep = Math.max(
        0,
        Math.min(Number(nextStep) || 0, STEPS.length - 1),
      );

      const previousStep = Number(state.current_step ?? 0);

      if (safeStep === previousStep) {
        return;
      }

      setState((current) => ({
        ...current,
        current_step: safeStep,
      }));

      try {
        const response = await portalApi.updateCurrentStep(token, safeStep);

        applyResponse(response);
      } catch (err) {
        console.error("Failed to save current portal step:", err);

        setState((current) => ({
          ...current,
          current_step: previousStep,
        }));

        toast.error("Unable to save the current step.");

        return;
      }

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    [
      state,
      applicationEditable,
      token,
      applyResponse,
      showEditingBlockedDialog,
    ],
  );

  const handleSubmitted = useCallback((submission) => {
    setState((current) => ({
      ...current,

      completed_steps: Array.from(
        new Set([
          ...(Array.isArray(current?.completed_steps)
            ? current.completed_steps
            : []),
          7,
        ]),
      ),

      status: "submitted",

      current_step: Number(current?.current_step ?? 7),

      declaration_accepted: true,

      declaration_locked: true,

      declaration_resubmission_required: false,

      portal: {
        ...(current?.portal || {}),
        declaration_accepted: true,
        declaration_accepted_at:
          submission?.submittedAt ?? new Date().toISOString(),
        submitted_at: submission?.submittedAt ?? new Date().toISOString(),
        declaration_resubmission_required: false,
        declaration_resubmission_reason: null,
      },

      submission: {
        applicationId: submission?.applicationId ?? current?.id ?? null,

        applicationNumber:
          submission?.applicationNumber ?? current?.application_number ?? null,

        submittedAt: submission?.submittedAt ?? new Date().toISOString(),
      },
    }));
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"
        data-testid="portal-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-[#0F2D52]" />
      </div>
    );
  }

  if (error || !state) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 bg-[#F8FAFC]"
        data-testid="portal-error"
      >
        <div className="max-w-md text-center bg-white rounded-3xl border border-gray-200 p-8">
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="h-6 w-6 text-red-500" />
          </div>

          <h1 className="text-xl font-bold font-heading text-[#0F2D52]">
            Link not accessible
          </h1>

          <p className="mt-2 text-sm text-gray-600">
            {error || "This application could not be loaded."}
          </p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    const props = {
      state,
      patch,
      setStep,
      token,
      onSubmitted: handleSubmitted,

      applicationEditable,
      declarationLocked,
      declarationResubmissionRequired,
    };

    switch (currentStep) {
      case 0:
        return (
          <WelcomeStep
            {...props}
            applicationStatus={applicationStatus}
            applicationStatusLabel={applicationStatusLabel}
            applicationEditable={applicationEditable}
          />
        );

      case 1:
        return <PersonalStep {...props} />;

      case 2:
        return <EmploymentStep {...props} />;

      case 3:
        return <ExistingLoansStep {...props} />;

      case 4:
        return <ReferencesStep {...props} />;

      case 5:
        return <AssetsStep {...props} />;

      case 6:
        return <DocumentsStep {...props} onUpdated={handleDocumentsUpdated} />;

      case 7:
        return <DeclarationStep {...props} />;

      // case 8:
      //   return <SubmittedStep {...props} />;

      default:
        return null;
    }
  };

  return (
    <main data-testid="portal-page" className="min-h-screen bg-[#F8FAFC]">
      <PortalHeader
        applicationNumber={state.application_number}
        applicantName={
          state.personal_details?.applicant_name ||
          state.personal_details?.fullName
        }
        saving={saving}
        savedAt={savedAt.current}
      />

      <Stepper
        steps={stepLabels}
        current={currentStep}
        completedSteps={completedSteps}
        pct={overallProgress}
        onNavigate={applicationEditable ? setStep : undefined}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{
              opacity: 0,
              y: 12,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              y: -8,
            }}
            transition={{
              duration: 0.25,
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="pb-16 text-center text-[11px] text-gray-500 px-6">
        <CheckCircle2 className="inline h-3 w-3 mr-1 text-[#16A34A]" />

        {submitted
          ? applicationEditable
            ? "Your declaration has been submitted. You can still update your application while it is under review."
            : "Your application is no longer editable."
          : "Your information is saved only when you click Save & Continue."}
      </div>

      <AlertDialog
        open={editingBlockedDialogOpen}
        onOpenChange={setEditingBlockedDialogOpen}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Application Editing Unavailable</AlertDialogTitle>

            <AlertDialogDescription className="space-y-3">
              <span className="block">
                Your application is currently in{" "}
                <strong className="text-[#0F2D52]">
                  {applicationStatusLabel}
                </strong>{" "}
                status.
              </span>

              <span className="block">
                You cannot edit your application while it is in this status.
              </span>

              <span className="block">
                If you need to make any changes, please contact your Money Mart
                representative.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogAction>OK, Got It</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FloatingActions />
    </main>
  );
}
